import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  ClipboardList,
  HandCoins,
  Hourglass,
  Network,
  Receipt,
  Store,
  Users,
} from "lucide-react";
import {
  ADMIN_USER_ROUTES,
  COMMISSION_ROUTES,
  FRANCHISE_ADMIN_ROUTES,
  adminFranchiseListResponseSchema,
  adminUserListResponseSchema,
  commissionListResponseSchema,
  formatPrice,
  topEarnersResponseSchema,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Button } from "@/shared/ui/core";
import { EmptyState } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  KpiCard,
  Td,
  Th,
  Tr,
} from "@/features/admin/components";
import { OrderStatusBadge } from "@/features/admin/orders/OrderStatusBadge";
import { formatOrderDate } from "@/features/admin/orders/format";
import { useAdminOrders, usePurchasesSummary } from "@/features/admin/orders/orders.queries";
import { RevenueOverviewCard } from "./RevenueOverviewCard";
import { TopEarnersCard } from "./TopEarnersCard";

const NO_PREFIX = { baseUrl: "" } as const;

/** Primer día del mes y hoy: el corte del resumen. */
function currentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El dashboard del panel administrador — LA pantalla del sistema
 * (Anahí, 27-ago, sobre referencias): la ganancia del mes dibujada en
 * la card violeta, los números clave (compras, usuarios, franquicias,
 * comisiones cubiertas y DEUDA en comisiones), el TOP de socios en la
 * card oscura, y las órdenes recientes.
 *
 * NO inventa datos: cada número sale de los MISMOS endpoints que sus
 * pantallas, así que el KPI y su sección nunca se contradicen.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminDashboardPage() {
  return (
    <AdminGuard>
      <DashboardScreen />
    </AdminGuard>
  );
}

function DashboardScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const month = useMemo(currentMonthRange, []);

  const summary = usePurchasesSummary({ page: 1, perPage: 1, ...month });
  const orders = useAdminOrders({ page: 1, perPage: 6 });
  const users = useQuery({
    queryKey: ["admin", "users", "count"],
    queryFn: () =>
      authFetch(`${ADMIN_USER_ROUTES.list}?page=1&perPage=1`, adminUserListResponseSchema, NO_PREFIX),
  });
  const franchises = useQuery({
    queryKey: ["admin", "franchises"],
    queryFn: () =>
      authFetch(FRANCHISE_ADMIN_ROUTES.list, adminFranchiseListResponseSchema, NO_PREFIX),
  });
  /* SIN filtro de estado: `totalsByStatus` trae pendiente, liberado y
   * pagado de una vez — la deuda y lo cubierto salen del mismo viaje. */
  const commissions = useQuery({
    queryKey: ["admin", "commissions", "totals"],
    queryFn: () =>
      authFetch(
        `${COMMISSION_ROUTES.adminList}?page=1&perPage=1`,
        commissionListResponseSchema,
        NO_PREFIX,
      ),
  });
  const top = useQuery({
    queryKey: ["admin", "commissions", "top"],
    queryFn: () => authFetch(COMMISSION_ROUTES.adminTop, topEarnersResponseSchema, NO_PREFIX),
  });

  const byStatus: Partial<Record<string, number>> = commissions.data?.totalsByStatus ?? {};
  const paidCents = byStatus["paid"] ?? 0;
  const owedCents = (byStatus["pending"] ?? 0) + (byStatus["released"] ?? 0);
  const recentOrders = orders.data?.items ?? [];
  const pendingOrders = recentOrders.filter((order) => order.status === "pending").length;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Dashboard")}
        description={t("El estado del negocio, de un vistazo. Cada número abre su sección.")}
      />

      {/* ── La fila protagonista: la ganancia y el top ────────────────── */}
      {/*
        `items-start` y no `items-stretch` (Anahí, 29-ago: "se ve mal
        esta card"). Estirada, el top de socios se alargaba hasta la
        altura de la card de ingresos y dejaba medio metro de negro
        vacío debajo de su única fila. Cada card mide lo que su
        contenido pide; el top crece solo cuando hay más socios.
      */}
      <div className="mb-4 grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <RevenueOverviewCard summary={summary.data} />
        <TopEarnersCard items={top.data?.items ?? []} />
      </div>

      {/* ── Los números del sistema ───────────────────────────────────── */}
      <div className="mb-4 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        <KpiCard
          icon={Receipt}
          label={t("Compras del mes")}
          value={String(summary.data?.totals.purchases ?? "—")}
        />
        <KpiCard icon={Users} label={t("Usuarios")} value={String(users.data?.total ?? "—")} />
        <KpiCard
          icon={Store}
          label={t("Franquicias adquiridas")}
          value={String(franchises.data?.items.length ?? "—")}
        />
        <KpiCard
          icon={HandCoins}
          label={t("Comisiones cubiertas")}
          value={formatPrice(paidCents, "MXN")}
        />
        <KpiCard
          icon={Hourglass}
          label={t("Deuda en comisiones")}
          value={formatPrice(owedCents, "MXN")}
        />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[1fr_320px]">
        {/* ── Órdenes recientes: lo primero que se atiende ────────────── */}
        <DataTableFrame aria-busy={orders.isFetching}>
          <div className="flex items-center justify-between px-5 pt-4">
            <h2 className="text-[15px] font-semibold text-ds-ink">
              {t("Órdenes recientes")}
              {pendingOrders > 0 && (
                <span className="ml-2 rounded-ds-pill bg-ds-mist px-2 py-0.5 text-[11px] font-semibold text-ds-violet-pop">
                  {t("{{n}} por validar", { n: pendingOrders })}
                </span>
              )}
            </h2>
            <Button as={Link} to="/admin/orders" variant="ghost" size="sm" rightIcon={<ArrowRight />}>
              {t("Ver todas")}
            </Button>
          </div>
          {recentOrders.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={<ClipboardList className="h-5 w-5 text-ds-ink-3" />}
                title={t("Sin órdenes todavía")}
                description={t("Cuando la tienda venda, aparecen aquí.")}
              />
            </div>
          ) : (
            <DataTable caption={t("Órdenes recientes")}>
              <thead>
                <tr>
                  <Th>{t("Folio")}</Th>
                  <Th>{t("Cliente")}</Th>
                  <Th align="right">{t("Total")}</Th>
                  <Th>{t("Estado")}</Th>
                  <Th align="right">{t("Fecha")}</Th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <Tr
                    key={order.id}
                    onClick={() => navigate(`/admin/orders/${order.id}`)}
                    className="cursor-pointer"
                  >
                    <Td className="font-mono text-[13px]">{order.folio}</Td>
                    <Td>{order.customer.name}</Td>
                    <Td align="right" className="font-medium">
                      {formatPrice(order.totalCents, order.currency)}
                    </Td>
                    <Td>
                      <OrderStatusBadge status={order.status} />
                    </Td>
                    <Td align="right" className="text-ds-ink-3">
                      {formatOrderDate(order.createdAt)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </DataTableFrame>

        {/* ── Accesos rápidos: a donde se trabaja ─────────────────────── */}
        <div className="grid gap-2">
          {[
            { to: "/admin/network", label: "Ver la red", icon: Network },
            { to: "/admin/commissions", label: "Libro de comisiones", icon: HandCoins },
            { to: "/admin/purchases", label: "Dashboard financiero", icon: Receipt },
            { to: "/admin/users", label: "Usuarios y KYC", icon: Users },
            { to: "/admin/franchises", label: "Franquicias", icon: Store },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group flex items-center gap-3 rounded-2xl border border-ds-border bg-white px-4 py-3 shadow-ds-float transition hover:border-ds-violet-pop"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-ds-mist text-ds-violet-pop">
                <item.icon className="h-4 w-4" />
              </span>
              <span className="flex-1 text-[14px] font-medium text-ds-ink">{t(item.label)}</span>
              <ArrowRight className="h-4 w-4 text-ds-ink-3 transition group-hover:text-ds-violet-pop" />
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}
